import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import * as S from './styles';
import Texts from '../Texts';
import ArrowForwardIcon from '@mui/icons-material/ArrowCircleLeft';
import { Button, CircularProgress, Backdrop, Modal, Box } from '@mui/material';
import { toast } from 'react-toastify';
import FileUpload from '../../helper/FileUpload';
import ImageUploadContainer from './ImageUploadContainer';
import uploadIcon from '/src/assets/images/application/folder-file-up.svg?url';
import { submitApplicationForm } from '../../thunks/submitApplicationForm';
import { RootState, AppDispatch } from '../../store';
import {
  setSubmitting,
  decrementStep,
  setCurrentStep,
  setNextStep,
  updateApplicationField,
} from '../../Slice/unifiedFormSlice';
import { useNavigate } from 'react-router-dom';
import { blue } from '@mui/material/colors';
import { useSubmitHandler } from '../../hooks/useSubmitHandler';
import SubmitButton from '../Button/SubmitButton';
import { ACTION_BLUE, ACTION_BLUE_HOVER } from '../../theme/theme';

// استایل‌های Modal
const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '98%', sm: '90%', md: '80%' },
  maxWidth: { xs: '380px', sm: '600px', md: '900px' },
  height: { xs: '40vh', sm: '40vh', md: '80vh' },
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: { xs: 0.5, sm: 2 },
  borderRadius: '8px',
  overflow: 'hidden',
};

// تایپ برای داده‌های فرم
interface FormValues {
  description: string;
  en_description: string;
  fa_description: string;
  subtitle_fa: string;
  subtitle_en: string;
  iconFile: File | null;
  imageFiles: File[];
}

// تایپ برای پاسخ سرور
interface ApplicationResponse {
  application?:
    | {
        id?: number;
        name_fa?: string | null;
        name_en?: string | null;
        logo?: string | null;
        version_text?: string | null;
      }
    | undefined;
}

// Validation schema با پیام‌های خطای فارسی
const validationSchema = Yup.object({
  description: Yup.string()
    .max(2000, 'توضیحات باید حداکثر ۲۰۰۰ کاراکتر باشد')
    .required('توضیحات الزامی است'),
  en_description: Yup.string()
    .max(2000, 'توضیحات انگلیسی باید حداکثر ۲۰۰۰ کاراکتر باشد')
    .required('توضیحات انگلیسی الزامی است'),
  fa_description: Yup.string()
    .max(2000, 'توضیحات فارسی باید حداکثر ۲۰۰۰ کاراکتر باشد')
    .required('توضیحات فارسی الزامی است'),
  subtitle_fa: Yup.string()
    .max(255, 'زیرعنوان فارسی باید حداکثر ۲۵۵ کاراکتر باشد')
    .required('زیرعنوان فارسی الزامی است'),
  subtitle_en: Yup.string()
    .max(255, 'زیرعنوان انگلیسی باید حداکثر ۲۵۵ کاراکتر باشد')
    .required('زیرعنوان انگلیسی الزامی است'),
  iconFile: Yup.mixed<File>()
    .required('فایل آیکون الزامی است')
    .test(
      'fileType',
      'آیکون باید فایل PNG باشد',
      (value) => value && value.type === 'image/png',
    ),
  imageFiles: Yup.array()
    .of(Yup.mixed<File>().required('فایل اسکرین‌شات الزامی است'))
    .min(1, 'حداقل یک اسکرین‌شات الزامی است')
    .required('اسکرین‌شات‌ها الزامی هستند'),
});

// تابع برای ذخیره داده‌های پاسخ سرور در Redux
const saveApplicationDataToRedux = (
  dispatch: AppDispatch,
  appData: ApplicationResponse['application'],
  existingApplicationId: string,
) => {
  console.group('Saving Application Data to Redux');
  const data = appData || {};
  const fields = [
    {
      field: 'application_id',
      value: String(data.id || existingApplicationId),
    },
    { field: 'fa_name', value: data.name_fa || '' },
    { field: 'en_name', value: data.name_en || '' },
    { field: 'logo', value: data.logo || null },
    { field: 'version_text', value: data.version_text || null },
  ] as const;

  fields.forEach(({ field, value }) => {
    console.log(`Saving ${field}:`, value);
    dispatch(updateApplicationField({ field, value }));
  });
  console.groupEnd();
};

// کامپوننت برای فیلدهای توضیحات
const DescriptionFields: React.FC<{
  formik: ReturnType<typeof useFormik<FormValues>>;
}> = ({ formik }) => (
  <>
    <S.Label>{Texts.ApplicationStepThree.lableShort}</S.Label>
    <S.TextFieldWrapper>
      <S.TextFieldBackground />
      <S.STextField
        id="description"
        name="description"
        type="text"
        placeholder={Texts.ApplicationStepThree.lableShort}
        value={formik.values.description}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        dir="rtl"
        error={formik.touched.description && !!formik.errors.description}
        helperText={formik.touched.description && formik.errors.description}
      />
    </S.TextFieldWrapper>

    {/*
      Subtitles were only editable after the fact, so every application came
      out of the wizard without one and then could not be saved from the edit
      form, which requires them. Collecting them here closes that gap.
    */}
    <S.Label>زیرعنوان فارسی</S.Label>
    <S.TextFieldWrapper>
      <S.TextFieldBackground />
      <S.STextField
        id="subtitle_fa"
        name="subtitle_fa"
        type="text"
        placeholder="زیرعنوان فارسی اپلیکیشن"
        value={formik.values.subtitle_fa}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        dir="rtl"
        inputProps={{ maxLength: 255 }}
        error={formik.touched.subtitle_fa && !!formik.errors.subtitle_fa}
        helperText={formik.touched.subtitle_fa && formik.errors.subtitle_fa}
      />
    </S.TextFieldWrapper>

    <S.Label>زیرعنوان انگلیسی</S.Label>
    <S.TextFieldWrapper>
      <S.TextFieldBackground />
      <S.STextField
        id="subtitle_en"
        name="subtitle_en"
        type="text"
        placeholder="English subtitle"
        value={formik.values.subtitle_en}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        dir="ltr"
        inputProps={{ maxLength: 255 }}
        error={formik.touched.subtitle_en && !!formik.errors.subtitle_en}
        helperText={formik.touched.subtitle_en && formik.errors.subtitle_en}
      />
    </S.TextFieldWrapper>

    <S.DiscriptionBox>
      <S.DescriptionFieldWrapper>
        <S.Label>{Texts.ApplicationStepThree.lableDescriptionEn}</S.Label>
        <S.DescriptionFieldLeft
          multiline
          sx={{
            // The wrapper is already half the row; 50% here made the field
            // a quarter of it and out of step with the Persian one.
            width: '100%',
            textAlign: 'left',
            justifyContent: 'start',
            paddingTop: '4px',
            alignContent: 'flex-end',
            height: '150px',
            overflowY: 'auto',
          }}
          id="en_description"
          name="en_description"
          type="text"
          placeholder={Texts.ApplicationStepThree.placeHolderEn}
          value={formik.values.en_description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          inputProps={{ maxLength: 10000 }}
          error={
            formik.touched.en_description && !!formik.errors.en_description
          }
          helperText={
            formik.touched.en_description && formik.errors.en_description
          }
        />
      </S.DescriptionFieldWrapper>
      <S.DescriptionFieldWrapper>
        <S.Label>{Texts.ApplicationStepThree.lableDescriptionFa}</S.Label>
        <S.DescriptionFieldRight
          sx={{
            width: '100%',
            paddingTop: '4px',
            height: '150px',
            overflowY: 'auto',
          }}
          multiline
          id="fa_description"
          name="fa_description"
          type="text"
          placeholder={Texts.ApplicationStepThree.placeHolderFa}
          value={formik.values.fa_description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          inputProps={{ maxLength: 10000 }}
          error={
            formik.touched.fa_description && !!formik.errors.fa_description
          }
          helperText={
            formik.touched.fa_description && formik.errors.fa_description
          }
        />
      </S.DescriptionFieldWrapper>
    </S.DiscriptionBox>
  </>
);

// کامپوننت برای آپلود فایل‌ها
const FileUploadSection: React.FC<{
  formik: ReturnType<typeof useFormik<FormValues>>;
  handleOpenModal: () => void;
}> = ({ formik, handleOpenModal }) => (
  <>
    <S.Title as="div">{Texts.applicationStepFour.titel}</S.Title>
    <S.IconBox>
      <S.LabelInit>
        <S.Label>{Texts.applicationStepFour.fomatImage} | </S.Label>
        <S.Label>{Texts.applicationStepFour.sizeImage}</S.Label>
      </S.LabelInit>
      <FileUpload
        height="40px"
        width="300px"
        compactView={true}
        showFileInfo={false}
        direction="rtl"
        flexDirection="row"
        onFilesSelected={(files: File[]) => {
          formik.setFieldValue('iconFile', files.length > 0 ? files[0] : null);
        }}
        imageSrc={uploadIcon}
        backgroundColor="#F0F0F0"
        uploadText={Texts.applicationStepFour.uploadIcon}
        acceptedFileTypes={['.png']}
      />
      {formik.touched.iconFile && formik.errors.iconFile && (
        <S.ErrorText>{String(formik.errors.iconFile)}</S.ErrorText>
      )}
    </S.IconBox>
    <S.DividerIcon />
    <S.IconBox>
      <S.LabelInit>
        <S.Label>{Texts.applicationStepFour.lableDescriptionFa}</S.Label>
      </S.LabelInit>
      <S.ButtonHelpApp onClick={handleOpenModal}>
        <S.IconHelp />
        {Texts.ApplicationStepTow.helplink}
      </S.ButtonHelpApp>
    </S.IconBox>
    <S.ImageBox>
      <ImageUploadContainer
        onImageUpload={(files) => {
          formik.setFieldValue('imageFiles', files);
        }}
      />
      {formik.touched.imageFiles && formik.errors.imageFiles && (
        <S.ErrorText>{String(formik.errors.imageFiles)}</S.ErrorText>
      )}
    </S.ImageBox>
  </>
);

const ApplicationFormComplate: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { application_id, publisher_id } = useSelector((state: RootState) => ({
    application_id: state.unifiedForm.application.application_id,
    publisher_id: state.unifiedForm.application.publisher_id,
  }));

  // مدیریت حالت Modal
  const [openModal, setOpenModal] = useState(false);
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  // استفاده از hook برای مدیریت submit
  const { isSubmitting, handleSubmit } = useSubmitHandler({
    successMessage: 'اطلاعات تکمیلی اپلیکیشن با موفقیت ارسال شد',
    debounceMs: 2000, // 2 ثانیه بین submit ها
    onSuccess: () => {
      // Land on step 4 (ApplicationStepFive, the success card) and stop there.
      //
      // The server's `next-step` is applications.step + 1 == 5, but Wizard.tsx
      // reads step 5 as "create an identity" — publishers.step and
      // applications.step are separate counters sharing one switch. Following
      // it sent a publisher who already has an identity straight back out to
      // the dashboard, so this card was never seen. Clearing nextStep stops
      // Wizard from preferring the server value; the user leaves via the
      // card's own button.
      setTimeout(() => {
        dispatch(setNextStep(null));
        dispatch(setCurrentStep(4));
        // mode must not be 'start' (the default when no state is passed):
        // Wizard.tsx redirects to the dashboard on that mode as soon as the
        // user has any identity, and a publisher submitting an app has one.
        // publisher_id goes along because Wizard bails out with an error for
        // any step >= 2 without one.
        navigate('/wizard', { state: { mode: 'end', id: publisher_id } });
      }, 800);
    },
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      description: '',
      en_description: '',
      fa_description: '',
      subtitle_fa: '',
      subtitle_en: '',
      iconFile: null,
      imageFiles: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!application_id || !publisher_id) {
        console.error('Missing required fields:', {
          application_id,
          publisher_id,
        });
        toast.error('اطلاعات اپلیکیشن یا ناشر موجود نیست.');
        return;
      }

      await handleSubmit(async () => {
        console.group('Submitting Application Form');
        console.log('Form values:', values);
        console.log('Submission payload:', { publisher_id, application_id });

        const response = await dispatch(
          submitApplicationForm({
            publisher_id,
            application_id,
            description: values.description,
            en_description: values.en_description,
            fa_description: values.fa_description,
            subtitle_fa: values.subtitle_fa,
            subtitle_en: values.subtitle_en,
            iconFile: values.iconFile,
            imageFiles: values.imageFiles,
          }),
        ).unwrap();
        console.log('Server response:', response);

        if (!response.application) {
          throw new Error('داده‌های اپلیکیشن در پاسخ سرور یافت نشد');
        }

        saveApplicationDataToRedux(
          dispatch,
          response.application,
          application_id,
        );

        console.groupEnd();
        return response;
      });
    },
  });

  // const handleCancel = () => {
  //   console.log('Cancel button clicked');
  //   dispatch(decrementStep());
  //   navigate('/identify', { state: { idedntty: 'personal', mode: 'start' } });
  // };

  // نمایش لودینگ تمام‌صفحه با Backdrop
  return (
    <S.Container>
      <Backdrop
        sx={{ color: '#a57b2b', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isSubmitting}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {!isSubmitting && (
        <>
          <S.Form onSubmit={formik.handleSubmit}>
            <DescriptionFields formik={formik} />
            <FileUploadSection
              formik={formik}
              handleOpenModal={handleOpenModal}
            />
            <S.SubmitBox>
              <SubmitButton
                type="submit"
                startIcon={<ArrowForwardIcon />}
                isSubmitting={isSubmitting}
                loadingText="در حال ارسال..."
                variant="contained"
                color="primary"
                sx={{
                  width: '180px',
                  backgroundColor: ACTION_BLUE,
                  color: '#FFFFFF',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: ACTION_BLUE_HOVER },
                }}
              >
                {Texts.applicationStepFour.submiteText}
              </SubmitButton>
            </S.SubmitBox>
          </S.Form>
          <Modal open={openModal} onClose={handleCloseModal}>
            <Box sx={modalStyle}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  paddingBottom: '56.25%',
                  overflow: 'hidden',
                }}
              >
                <iframe
                  src="https://www.aparat.com/video/video/embed/videohash/f61g195/vt/frame"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  allowFullScreen={true}
                />
              </Box>
            </Box>
          </Modal>
        </>
      )}
    </S.Container>
  );
};

export default ApplicationFormComplate;
